// Libraries
import React from 'react';
import { Form, Formik } from 'formik';
// Components
import { CheckBox, Input, SelectInput } from '@components/input';
import { Button } from '@components/button';
import { Modal, ModalImportantInfo } from '@components/modal';
import { Loader } from '@components/loader';
import { ModalPoliticalData } from '../modal-political-data/ModalPoliticalData';
// Images
import loader from '@assets/icons/spinner-white.gif';
import nelLogo from '@assets/logos/nel-logo.png';
// Models
import { IValidateSalary } from '@pages/employees/models/Employee';
import { IDataCompany } from '../models/DataCompany';
// Hooks
import { useDataCompany } from '../hooks/useDataCompany';
import { useValidateDocumentNumber } from '@hooks/useValidateDocumentNumber';
import { useValidateDataCompany } from '../hooks/useValidateDataCompany';
// Constants
import { NOMINA_EN_LINEA_PRODUCT, OnboardingStepsUser, TypeDocumentNames } from '@constants/Onboarding';
import { TypesDocumentIds } from '@pages/employees/constants/Employees';
// Styles
import './DataCompany.scss';
import { toUpperCaseLetters } from '@utils/Text';
import { COMPANY_DATA_FORM, IMG_STEP } from '@constants/Utils';
import { useTaxpayerAutofill } from '@hooks/useAutofillTaxpayer';

export const DataCompany: React.FC = () => {
    const {
        plansOptions,
        documentTypeOptions,
        openCompanyErrorMessage,
        showLoader,
        showCustomLoader,
        currentStep,
        modalErrorRequest,
        loader: loaderStatus,
        shouldShowFreeDemo,
        handleCompanyCreationSubmit,
        handleLogOut,
        openDocumentPdf,
        handleSubmitDocumentsApprovement,
        handleRefreshPage,
    } = useDataCompany();
    const { validationSchema, validateNit } = useValidateDataCompany();
    const { getMaxLengthDocumentByType, handleValidateNitDocumentNumberChange } = useValidateDocumentNumber();
    const { COMPANY_DATA, POLITICAL_DATA } = OnboardingStepsUser;

    return (
        <div className="data-company">
            <ModalImportantInfo
                isOpen={openCompanyErrorMessage}
                onAfterOpen={(): void => {}}
                onRequestClose={handleLogOut}
                className="informative--modal"
                handleBack={handleLogOut}
            >
                <div className="content__modal">
                    <div className="section__text">
                        <p className="text">Si olvidaste tu usuario regresa al inicio de sesión y da clic en recordar usuario.</p>
                    </div>
                </div>
            </ModalImportantInfo>
            <Loader isOpen={showLoader || loaderStatus} />
            <img src={nelLogo} className="img-top-header" alt="Nómina en línea" />
            {currentStep === COMPANY_DATA && !modalErrorRequest && !loaderStatus && (
                <div className="page-content">
                    <Formik
                        initialValues={
                            {
                                company_name: '',
                                document_type_id: '',
                                document_number: '',
                                dv: '',
                                plan_type_id: '',
                                first_name: '',
                                second_name: '',
                                first_last_name: '',
                                second_last_name: '',
                                data_processing: false,
                                free_demo: false,
                            } as IDataCompany
                        }
                        onSubmit={(values, { resetForm }): void => {
                            handleCompanyCreationSubmit(values);
                            resetForm();
                        }}
                        validationSchema={validationSchema}
                        validate={(values): object | IValidateSalary => validateNit(values)}
                    >
                        {({ values, setFieldValue, resetForm }): JSX.Element => {
                            const { searchTaxpayer } = useTaxpayerAutofill({
                                fieldMap: COMPANY_DATA_FORM,
                                setFieldValue,
                            });
                            return (
                                <Form>
                                    <div className="data-form">
                                        <label className="title--form">Datos empresa</label>
                                        <img src={IMG_STEP} className="img-progress" alt="Progress" />
                                        <div className="container__inputs">
                                            <SelectInput
                                                name="document_type_id"
                                                options={documentTypeOptions}
                                                isSearchable
                                                label="Tipo de documento"
                                                classLabel="label-custom"
                                                selectClassContainer="custom--inputs"
                                                onChange={(item): void => {
                                                    const value = (item as { value: string; label: string }).value;
                                                    resetForm({
                                                        values: {
                                                            ...values,
                                                            document_type_id: value,
                                                            company_name: '',
                                                            first_name: '',
                                                            second_name: '',
                                                            first_last_name: '',
                                                            second_last_name: '',
                                                            dv: '',
                                                            document_number: '',
                                                        },
                                                    });
                                                }}
                                            />
                                            <Input
                                                name="document_number"
                                                label="Número de documento"
                                                inputClassContainer="custom--inputs"
                                                type="number"
                                                maxLength={getMaxLengthDocumentByType(values.document_type_id)}
                                                onChange={(e): void =>
                                                    handleValidateNitDocumentNumberChange(
                                                        e as React.ChangeEvent<HTMLInputElement>,
                                                        values.document_type_id,
                                                        setFieldValue,
                                                        'document_number',
                                                        'dv'
                                                    )
                                                }
                                                onBlur={(e: React.FocusEvent<HTMLInputElement>): void => {
                                                    searchTaxpayer(values.document_type_id, e.target.value);
                                                }}
                                            />
                                            {values.document_type_id === TypesDocumentIds.NIT && (
                                                <Input
                                                    name="dv"
                                                    label="DV"
                                                    inputClassContainer="dv--input"
                                                    maxLength={1}
                                                    type="number"
                                                    disabled={true}
                                                />
                                            )}
                                        </div>
                                        {values.document_type_id === TypesDocumentIds.NIT && (
                                            <div className="container__inputs">
                                                <Input
                                                    name="company_name"
                                                    inputClassContainer="reason--input"
                                                    label="Razón social"
                                                    onChange={(e): void => {
                                                        const input = (e as React.ChangeEvent<HTMLInputElement>).target;
                                                        const start = input.selectionStart ?? 0;
                                                        const value = input.value.toUpperCase();

                                                        setFieldValue('company_name', value);

                                                        requestAnimationFrame(() => {
                                                            input.setSelectionRange(start, start);
                                                        });
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {values.document_type_id === TypesDocumentIds.CC && (
                                            <>
                                                <div className="container__inputs">
                                                    <Input
                                                        name="first_name"
                                                        label="Primer nombre"
                                                        inputClassContainer="custom--inputs"
                                                        onChange={(e): void => {
                                                            const value = (e as React.ChangeEvent<HTMLInputElement>).target.value;
                                                            setFieldValue('first_name', toUpperCaseLetters(value, false));
                                                        }}
                                                    />
                                                    <Input
                                                        name="second_name"
                                                        label="Segundo nombre (opcional)"
                                                        inputClassContainer="custom--inputs"
                                                        onChange={(e): void => {
                                                            const value = (e as React.ChangeEvent<HTMLInputElement>).target.value;
                                                            setFieldValue('second_name', toUpperCaseLetters(value, false));
                                                        }}
                                                    />
                                                </div>

                                                <div className="container__inputs">
                                                    <Input
                                                        name="first_last_name"
                                                        label="Primer apellido"
                                                        inputClassContainer="custom--inputs"
                                                        onChange={(e): void => {
                                                            const value = (e as React.ChangeEvent<HTMLInputElement>).target.value;
                                                            setFieldValue('first_last_name', toUpperCaseLetters(value, false));
                                                        }}
                                                    />
                                                    <Input
                                                        name="second_last_name"
                                                        label="Segundo apellido (opcional)"
                                                        inputClassContainer="custom--inputs"
                                                        onChange={(e): void => {
                                                            const value = (e as React.ChangeEvent<HTMLInputElement>).target.value;
                                                            setFieldValue('second_last_name', toUpperCaseLetters(value, false));
                                                        }}
                                                    />
                                                </div>
                                            </>
                                        )}
                                        <div className="container__inputs">
                                            <SelectInput
                                                name="plan_type_id"
                                                options={plansOptions}
                                                isSearchable
                                                label="Cantidad de empleados"
                                                classLabel="label-custom"
                                                selectClassContainer="custom--inputs"
                                            />
                                        </div>
                                        <div className="modal-term__container--check">
                                            <div>
                                                <CheckBox name="data_processing" checked={values.data_processing} />
                                            </div>
                                            <div>
                                                He leído y autorizo el&nbsp;
                                                <span
                                                    onClick={(): void =>
                                                        openDocumentPdf(
                                                            TypeDocumentNames.DATA_PROCESSING,
                                                            NOMINA_EN_LINEA_PRODUCT
                                                        )
                                                    }
                                                >
                                                    Tratamiento de Datos Personales.
                                                </span>
                                            </div>
                                        </div>
                                        <div className="modal-term__container--check">
                                            <div>
                                                <CheckBox name="free_demo" checked={values.free_demo} />
                                            </div>
                                            <div>
                                                He leído y acepto la&nbsp;
                                                <span
                                                    onClick={(): void =>
                                                        openDocumentPdf(TypeDocumentNames.FREE_DEMO, NOMINA_EN_LINEA_PRODUCT)
                                                    }
                                                >
                                                    Declaración de Uso Gratuito del Producto.
                                                </span>
                                            </div>
                                        </div>
                                        <div
                                            className={`container__button ${
                                                showCustomLoader && 'container__button--loader-margin'
                                            }`}
                                        >
                                            <Button type="submit" disabled={!(values.free_demo && values.data_processing)}>
                                                Aceptar
                                            </Button>
                                        </div>
                                        {showCustomLoader && (
                                            <div className="custom-loader">
                                                <img src={loader} alt="loader" />
                                                Estamos procesando tu solicitud
                                            </div>
                                        )}
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                </div>
            )}
            {currentStep === POLITICAL_DATA && !modalErrorRequest && !loaderStatus && (
                <ModalPoliticalData
                    showFreeDemoDocument={shouldShowFreeDemo}
                    handleOpenDocument={openDocumentPdf}
                    handleSubmitDocuments={handleSubmitDocumentsApprovement}
                />
            )}
            <Modal isOpen={modalErrorRequest || false} closeIcon={false}>
                <div className="data-company__modal-redirect">
                    En este momento no podemos completar tu proceso de registro. Intentalo nuevamente.
                    <div className="data-company__modal-redirect--button-container">
                        <Button onClick={handleRefreshPage}>Aceptar</Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
